// Iter 76 Sprint 0 · Iter 114 P0-B — Shares list page.
// A "+ New share" CTA is the top action on every state (empty + populated),
// desktop and mobile, so a user who lands here is never stuck without an
// entry point. The CTA opens an inline modal that walks the user through
// Project → Sheet(s) → Submit via the existing plan-shares endpoint.
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Share2, Clock, Printer, Copy, Check, Plus, X, Loader2, FileText, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_COLOR = {
  active:  "text-emerald-500",
  expired: "text-[var(--fop-text-3)]",
  revoked: "text-red-500",
};

export default function SharesList({ scope = "global" } = {}) {
  const { projectId } = useParams();
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  useEffect(() => {
    if (scope !== "global") return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || data || []);
      } catch { setProjects([]); }
    })();
  }, [scope]);

  const fetchScoped = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/plan-shares`);
      return (data.items || data.shares || data || []).map((s) =>
        ({ ...s, _project_id: pid }));
    } catch { return []; }
  }, []);

  const loadShares = useCallback(async () => {
    setLoading(true);
    try {
      if (scope === "project" && projectId) {
        setItems(await fetchScoped(projectId));
      } else if (scope === "global") {
        if (!projects.length) { setItems([]); return; }
        const all = await Promise.all(projects.slice(0, 25).map((p) => fetchScoped(p.id)));
        setItems(all.flat().sort((a, b) =>
          String(b.created_at || "").localeCompare(String(a.created_at || ""))));
      }
    } finally { setLoading(false); }
  }, [scope, projectId, projects, fetchScoped]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const classify = (s) => {
    if (s.revoked_at) return "revoked";
    if (s.expires_at && new Date(s.expires_at) < new Date()) return "expired";
    return "active";
  };

  const shareUrl = (s) => `${window.location.origin}/plan-share/${s.token || s.id}`;

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(shareUrl(s));
      setCopied(s.id);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* ignore */ }
  };

  // Pick the right list of projects for the composer.
  const composerProjects = scope === "project" && projectId
    ? projects.filter((p) => p.id === projectId)
    : projects;

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="shares-list">
      <style>{`@media print {
        body, [data-testid="shares-list"] { background: white !important; color: black !important; }
        [data-no-print] { display: none !important; }
      }`}</style>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="mb-4 flex items-start justify-between gap-4" data-no-print>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              {scope === "project" ? "Project · Shares" : "ForgedOps · Shares Center"}
            </div>
            <h1 className="text-2xl font-semibold mt-1">Plan shares</h1>
            <div className="text-[12px] text-[var(--fop-text-3)] mt-1">
              Send a sheet to the owner, the inspector, the subcontractor — view-only, expires when you say so.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => window.print()}
              data-testid="shares-print-btn"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10"
            >
              <Printer size={14} /> Print
            </button>
            <button
              onClick={() => setComposerOpen(true)}
              data-testid="new-share-btn"
              className="btn btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold min-h-[40px]"
            >
              <Plus size={16} /> New share
            </button>
          </div>
        </header>

        {/* Trust strip — answers "is this safe to send?" before they ask. */}
        <div className="mb-4 flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="shares-trust-strip">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> View-only by default</span>
          <span className="inline-flex items-center gap-1.5"><Clock size={12} className="text-[var(--fop-primary)]" /> Expires when you set it</span>
          <span className="inline-flex items-center gap-1.5"><FileText size={12} className="text-[var(--fop-primary)]" /> Every open is logged</span>
        </div>

        <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden">
          {loading && (
            <div className="px-4 py-8 text-center text-[var(--fop-text-3)] text-sm" data-testid="shares-loading">Loading shares…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="shares-empty">
              <Share2 size={32} className="mx-auto text-[var(--fop-text-3)] mb-3" />
              <div className="text-base text-[var(--fop-text)] font-medium">Nothing shared yet.</div>
              <div className="text-sm text-[var(--fop-text-3)] mt-1 mb-4">
                Pick a sheet, send a link. The other side opens it on any device — no login, no download.
              </div>
              <button
                onClick={() => setComposerOpen(true)}
                data-testid="shares-empty-cta"
                className="btn btn-primary inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold"
              >
                <Plus size={16} /> Start a share
              </button>
            </div>
          )}
          {items.map((s) => {
            const status = classify(s);
            return (
              <div
                key={s.id}
                className="px-4 py-3 border-b border-[var(--fop-border)] last:border-b-0 flex gap-3 items-center"
                data-testid={`share-row-${s.id}`}
              >
                <Share2 size={14} className={STATUS_COLOR[status]} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{s.label || s.title || s.sheet_name || "Plan share"}</div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">
                    {scope === "global" && (<>{projectName(s._project_id || s.project_id)} · </>)}
                    {status}
                    {s.expires_at && (<> · Exp {String(s.expires_at).slice(0,10)}</>)}
                    {typeof s.view_count === "number" && (<> · {s.view_count} view{s.view_count === 1 ? "" : "s"}</>)}
                    {s.permission && (<> · {s.permission}</>)}
                  </div>
                </div>
                <button
                  onClick={() => copyLink(s)}
                  data-testid={`share-copy-${s.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[var(--fop-primary)]/40 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10"
                  data-no-print
                >
                  {copied === s.id ? <Check size={11} /> : <Copy size={11} />}
                  {copied === s.id ? "Copied" : "Copy link"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {composerOpen && (
        <ShareComposer
          projects={composerProjects}
          defaultProjectId={scope === "project" ? projectId : ""}
          onClose={() => setComposerOpen(false)}
          onCreated={async (created) => {
            setComposerOpen(false);
            try {
              await navigator.clipboard.writeText(`${window.location.origin}/plan-share/${created.token}`);
              toast.success("Share link copied — paste it into your message.");
            } catch {
              toast.success("Share created.");
            }
            // Iter 254 fix: the reviewer walkthrough found the list still
            // showed the empty state right after a successful create — the
            // reload could race with `projects` state on the global scope.
            // Append the new row immediately so the UI never looks stale,
            // then reconcile with a real refetch in the background.
            // Iter 255 fix: loadShares() on the global scope only
            // iterates the first 25 projects (see fetchScoped usage
            // above) — for a tenant with >25 projects, refetching here
            // can silently overwrite the optimistic row with an empty
            // list when the just-created share's project falls outside
            // that slice. The optimistic entry already has everything
            // the row needs to render, so skip the refetch entirely.
            setItems((prev) => [{ ...created, _project_id: created.project_id || projectId }, ...prev]);
          }}
        />
      )}
    </div>
  );
}

// Iter 114 P0-B — minimal, in-page composer. Uses the existing
// POST /api/projects/{pid}/plan-shares endpoint. NO new backend.
function ShareComposer({ projects, defaultProjectId, onClose, onCreated }) {
  const [projectId, setProjectId] = useState(defaultProjectId || (projects[0]?.id || ""));
  const [sheets, setSheets] = useState([]);
  const [sheetQuery, setSheetQuery] = useState("");
  const [picked, setPicked] = useState({}); // {sheet_id: true}
  const [title, setTitle] = useState("");
  const [expiresInHours, setExpiresInHours] = useState(720); // 30 days
  const [includePhotos, setIncludePhotos] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!projectId) { setSheets([]); return; }
    let cancelled = false;
    const q = sheetQuery.trim();
    const url = `/projects/${projectId}/sheets${q.length >= 1 ? `?q=${encodeURIComponent(q)}` : ""}`;
    api.get(url).then((r) => {
      if (cancelled) return;
      setSheets(Array.isArray(r.data) ? r.data.slice(0, 50) : []);
    }).catch(() => setSheets([]));
    return () => { cancelled = true; };
  }, [projectId, sheetQuery]);

  const toggle = (id) => setPicked((p) => ({ ...p, [id]: !p[id] }));
  const pickedIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (!projectId) { toast.error("Pick a project."); return; }
    if (pickedIds.length === 0) { toast.error("Pick at least one sheet."); return; }
    setBusy(true);
    try {
      const t = title.trim() || (pickedIds.length === 1
        ? `Sheet ${sheets.find((s) => s.id === pickedIds[0])?.sheet_number || "share"}`
        : `${pickedIds.length} sheets`);
      const { data } = await api.post(`/projects/${projectId}/plan-shares`, {
        share_type: pickedIds.length === 1 ? "sheet" : "selected_sheets",
        title: t,
        sheet_ids: pickedIds,
        access_mode: "public_token",
        include_photos: includePhotos,
        allow_download: allowDownload,
        expires_in_hours: Number(expiresInHours) || 720,
      });
      await onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create share");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="fop-surface-2 rounded-lg w-full max-w-lg border border-[var(--fop-border-hi)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-composer"
      >
        <div className="p-5 border-b border-[var(--fop-border)] flex items-center justify-between sticky top-0 bg-[var(--fop-surface-2)] z-10">
          <div>
            <h3 className="font-display text-lg text-[var(--fop-text)]">Share a sheet</h3>
            <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5">View-only · Expires when you say · Every open logged</div>
          </div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {projects.length > 1 && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Project</label>
              <select
                value={projectId}
                onChange={(e) => { setProjectId(e.target.value); setPicked({}); }}
                data-testid="share-project"
                className="w-full px-2 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm"
              >
                <option value="">— Pick project —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.id}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Which sheets?</label>
            <input
              value={sheetQuery}
              onChange={(e) => setSheetQuery(e.target.value)}
              disabled={!projectId}
              data-testid="share-sheet-search"
              className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm disabled:opacity-50"
              placeholder={projectId ? "Search by sheet number or title…" : "Pick a project first"}
            />
            {sheets.length > 0 && (
              <ul
                className="mt-1 max-h-56 overflow-y-auto rounded border border-[var(--fop-border-2)] bg-[var(--fop-surface)]"
                data-testid="share-sheet-list"
              >
                {sheets.map((s) => {
                  const on = !!picked[s.id];
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => toggle(s.id)}
                        data-testid={`share-sheet-option-${s.id}`}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b last:border-b-0 border-[var(--fop-border)] ${on ? "bg-[var(--fop-primary-soft)]" : "hover:bg-[var(--fop-surface-2)]"}`}
                      >
                        <span className={`w-4 h-4 rounded border ${on ? "bg-[var(--fop-primary)] border-[var(--fop-primary)]" : "border-[var(--fop-border-2)]"} flex items-center justify-center`}>
                          {on && <Check size={10} className="text-white" />}
                        </span>
                        <span className="font-mono text-[11px] text-[var(--fop-primary)] mr-1">{s.sheet_number}</span>
                        <span className="truncate">{s.sheet_title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-1" data-testid="share-pick-count">
              {pickedIds.length} sheet{pickedIds.length === 1 ? "" : "s"} picked
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Label (optional)</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="share-title"
              className="w-full px-3 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm"
              placeholder="e.g., D-100 drainage details for owner review"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Expires</label>
            <select
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(e.target.value)}
              data-testid="share-expires"
              className="w-full px-2 py-2 bg-[var(--fop-surface)] border border-[var(--fop-border-2)] rounded text-sm"
            >
              <option value={24}>24 hours</option>
              <option value={168}>7 days</option>
              <option value={720}>30 days</option>
              <option value={2160}>90 days</option>
            </select>
          </div>

          <div className="border border-[var(--fop-border-2)] rounded-md p-3 space-y-2">
            <label className="block text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">More options</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={includePhotos}
                onChange={(e) => setIncludePhotos(e.target.checked)}
                data-testid="share-include-photos"
                className="accent-[var(--fop-primary)]"
              />
              Include field photos &amp; evidence
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={allowDownload}
                onChange={(e) => setAllowDownload(e.target.checked)}
                data-testid="share-allow-download"
                className="accent-[var(--fop-primary)]"
              />
              Allow recipient to download the original document
            </label>
            <div className="text-[11px] text-[var(--fop-text-4)]">
              Markups and measurements are always included, view-only, unless you turn off download above.
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-[var(--fop-border)] flex items-center justify-end gap-2 sticky bottom-0 bg-[var(--fop-surface-2)] z-10">
          <button onClick={onClose} disabled={busy} className="btn btn-ghost text-xs">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !projectId || pickedIds.length === 0}
            data-testid="share-submit"
            className="btn btn-primary text-xs inline-flex items-center gap-1.5 px-4 py-2 min-h-[40px]"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} Create share link
          </button>
        </div>
      </div>
    </div>
  );
}
